import { Link } from "react-router-dom";

interface Crumb {
  label: string;
  href?: string;
}

interface PageHeroProps {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  crumbs: Crumb[];
  image?: string;
}

const DEFAULT_IMG =
  "/uploads/hero-fleet-banner.jpeg";

export default function PageHero({ title, subtitle, eyebrow, crumbs, image }: PageHeroProps) {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={image || DEFAULT_IMG}
          alt={title}
          title={title}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-accent-950/95 via-accent-950/88 to-accent-900/75" />
      </div>

      <div className="relative mx-auto max-w-screen-2xl px-4 py-12 md:px-6 md:py-16">
        <nav aria-label="Breadcrumb" className="mb-5">
          <ol className="flex flex-wrap items-center gap-2 text-xs md:text-sm">
            {crumbs.map((crumb, index) => (
              <li key={crumb.label} className="flex items-center gap-2">
                {crumb.href ? (
                  <Link to={crumb.href} className="text-background-300 transition-colors hover:text-primary-300">
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="font-medium text-primary-300">{crumb.label}</span>
                )}
                {index < crumbs.length - 1 && (
                  <i className="ri-arrow-right-s-line text-background-400" aria-hidden="true" />
                )}
              </li>
            ))}
          </ol>
        </nav>

        {eyebrow && (
          <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-primary-200">
            <i className="ri-roadster-line" aria-hidden="true" />
            {eyebrow}
          </span>
        )}

        <h1 className="max-w-4xl font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
          {title}
        </h1>

        {subtitle && (
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-background-200 md:text-base">{subtitle}</p>
        )}
      </div>
    </section>
  );
}